import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Briefcase, ChevronLeft, Info, Loader2, Search } from "lucide-react";
import { API, JURISDICTIONS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const AttorneyAnalyticsPage = () => {
  const [form, setForm] = useState({ attorney_name: "", jurisdiction: "federal", bar_number: "", firm_name: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const analyze = async () => {
    if (!form.attorney_name.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/attorneys/analyze`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="attorney-analytics-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Attorney Litigation Profile</h1>
            <Badge className="bg-purple-500">LIVE DATA</Badge>
          </div>
          <p className="text-muted-foreground">Historical practice pattern analysis from public records</p>
        </div>
        
        <Alert className="mb-6 bg-slate-100 border-slate-200">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This tool analyzes historical practice patterns from court filings and public records. 
            It provides factual statistics only and does not assess competence, ethics, or character.
          </AlertDescription>
        </Alert>
        
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle>Search Attorney</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Attorney Name</Label>
                <Input 
                  value={form.attorney_name} 
                  onChange={e => setForm(p => ({ ...p, attorney_name: e.target.value }))}
                  placeholder="e.g., Jane Doe, Esq."
                />
              </div>
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Bar Number (Optional)</Label>
                <Input 
                  value={form.bar_number} 
                  onChange={e => setForm(p => ({ ...p, bar_number: e.target.value }))}
                  placeholder="e.g., 123456"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Firm Name (Optional)</Label>
                <Input 
                  value={form.firm_name} 
                  onChange={e => setForm(p => ({ ...p, firm_name: e.target.value }))}
                  placeholder="e.g., Smith & Associates"
                />
              </div>
              <Button className="w-full" onClick={analyze} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Briefcase className="h-4 w-4 mr-2" />}
                Analyze Patterns
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <div className="lg:col-span-2 space-y-4 animate-fade-in">
              <Card>
                <CardHeader>
                  <CardTitle>{result.attorney_name}</CardTitle>
                  <CardDescription>
                    {result.firm_name && `${result.firm_name} • `}
                    {result.jurisdiction}
                    {result.bar_number && ` • Bar #${result.bar_number}`}
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader><CardTitle>Case Volume by Type</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(result.case_volume_by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center gap-3">
                        <span className="text-sm capitalize w-40">{type.replace(/_/g, ' ')}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full" 
                            style={{ width: `${Math.min((count / 50) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-10 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader><CardTitle>Motion Practice</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm">Motions per Case (Avg)</span>
                        <span className="font-semibold">{result.motion_practice_frequency.motions_filed_per_case_avg}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Opposition Response Rate</span>
                        <span className="font-semibold">{(result.motion_practice_frequency.opposition_response_rate * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Success Rate on Motions</span>
                        <span className="font-semibold">{(result.motion_practice_frequency.success_rate_on_motions * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader><CardTitle>Duration Patterns</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm">Avg Case Duration</span>
                        <span className="font-semibold">{result.litigation_duration_patterns.average_case_duration_months} months</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Cases to Trial</span>
                        <span className="font-semibold">{(result.litigation_duration_patterns.cases_to_trial * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Pre-Suit Settlement</span>
                        <span className="font-semibold">{(result.litigation_duration_patterns.cases_settled_pre_suit * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader><CardTitle>Procedural Tendencies</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {Object.entries(result.procedural_tendencies).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                        <Badge variant="outline">{String(value)}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default AttorneyAnalyticsPage;
